import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import * as jose from 'jose';
import { isValidDate } from './utils';

const selectSessionState = (state: RootState) => state.session;

export const selectUserSignInStatus = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.userLoggedIn,
);

export const selectEntitlements = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.entitlements,
);

export const selectAccessToken = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.accessToken,
);

export const selectRefreshToken = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.refreshToken,
);

export const selectToken = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.token,
);

export const selectAgentId = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.agentId,
);

export const selectMissingPii = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.missingPii,
);

export const selectIsUserAgent = createSelector(
  [selectAgentId],
  (agentId) => !!agentId,
);

export const selectUserFeatures = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.features,
);

export const selectIsEnrollmentLogin = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.isEnrollmentLogin,
);

export const selectUserOffline = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.userOffline,
);

// vault selectors

export const getAccessTokenDecoded = (state: RootState) => {
  if (!state.session.accessToken) return '';
  const accessToken = jose.decodeJwt(state.session.accessToken);
  if (typeof accessToken === 'object' && accessToken) {
    return accessToken as any;
  }
  return '';
};

export const getAccessTokenAlias = (state: RootState) => {
  if (!state.session.accessToken) return '';
  const accessToken = getAccessTokenDecoded(state);
  if (typeof accessToken === 'object' && accessToken) {
    return accessToken.alias;
  }
  return '';
};

export const getAccessTokenExpires = (state: RootState) => {
  if (!state.session.accessToken) return new Date(0);
  const accessToken = getAccessTokenDecoded(state);
  if (typeof accessToken === 'object' && accessToken) {
    if (accessToken.exp && isValidDate(new Date(accessToken.exp * 1e3))) {
      return new Date(accessToken.exp * 1e3);
    }
    return new Date(0);
  }
  return new Date(0);
};

export const getIsAccessTokenValid = (state: RootState) => {
  const accessTokenExpires = getAccessTokenExpires(state);
  return accessTokenExpires.getTime() > Date.now();
};
